import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  Stack,
  Typography
} from "@mui/material";
import { Entity, EntityItem, MediaItemDetailed, MediaResponse, RelatedEntityItem } from "../../types";
import { Circle, LinkOffOutlined } from "@mui/icons-material";

type MediaUnlinkDialogProps = {
  item: Partial<Entity | EntityItem | MediaResponse | MediaItemDetailed> | undefined;
  onUnlink: () => Promise<void> | void;
  onCancel: () => void;
  relationships: { current: RelatedEntityItem[]; updated: RelatedEntityItem[] };
  open: boolean;
  isPending?: boolean;
};

export default function MediaUnlinkDialog({ item, relationships, onUnlink, onCancel, open, isPending }: MediaUnlinkDialogProps) {
  return (
    <Dialog
      open={open}
      PaperProps={{
        sx: {
          borderRadius: "0.5rem",
          paddingBlock: "0.75rem"
        }
      }}
      onClose={onCancel}
    >
      <DialogTitle sx={{ color: "warning.main", display: "flex", alignItems: "flex-start", gap: "0.5rem" }}>
        <Typography variant='h5' fontWeight={500} sx={{ lineBreak: "anywhere" }}>
          Remove Link
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack gap={"1rem"}>
          <Typography variant='body1' component='div' sx={{ mb: "0.5rem" }}>
            You are about to update the relationships for this item and {item?.name}.
          </Typography>

          <Box>
            <Typography variant='subtitle1' gutterBottom component='div'>
              Current Relationships:
            </Typography>
            {
              relationships?.current?.length > 0 ? (
                <Box sx={{ mb: 2 }}>
                  <List sx={{ maxHeight: "10rem", overflowY: "auto" }} className='scroller'>
                    {relationships?.current?.map((item, index) => (
                      <ListItem key={`delete-${item.id}-${index}`} sx={{ paddingBlock: "0.075rem", alignItems: "baseline" }}>
                        <Circle sx={{ fontSize: "0.4rem", color: "grey.400", mr: 12, mt: 3 }} />
                        <Typography variant='subtitle1' color='grey.600'>
                          {item.entity_name || item.name || `ID: ${item.id}`}
                        </Typography>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              ) : (
                <Typography variant='body2' component='div' sx={{ mb: 2 }}>
                  No relationships found.
                </Typography>
              )
            }
          </Box>

          <Box>
            <Typography variant='subtitle1' gutterBottom component='div'>
              Updated Relationships:
            </Typography>

            {
              // Collect all relationships from the media item
              // Performance: Delete dialog logging reduced to minimize duplicate output
              relationships?.updated?.length > 0 ? (
                <Box sx={{ mb: 2 }}>
                  <List sx={{ maxHeight: "10rem", overflowY: "auto" }} className='scroller'>
                    {relationships?.updated?.map((item, index) => (
                      <ListItem key={`delete-${item.id}-${index}`} sx={{ paddingBlock: "0.075rem", alignItems: "baseline" }}>
                        <Circle sx={{ fontSize: "0.4rem", color: "grey.400", mr: 12, mt: 3 }} />
                        <Typography variant='subtitle1' color='grey.600'>
                          {item.entity_name || item.name || `ID: ${item.id}`}
                        </Typography>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              ) : (
                <Typography variant='body2' component='div' sx={{ mb: 2 }}>
                  No relationships will remain.
                </Typography>
              )
            }
          </Box>

          <Typography
            variant='body1'
            component='div'
            sx={{
              mt: 2,
              color: "warning.main",
              borderRadius: 1
            }}
          >
            Do you want to proceed with these changes?
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ paddingInline: "1.5rem" }}>
        <Button onClick={onCancel} variant='secondary-outlined' disabled={isPending}>
          Cancel
        </Button>
        <Button
          onClick={onUnlink}
          color='warning'
          variant='contained'
          disableElevation
          startIcon={isPending ? <CircularProgress size={12} sx={{ color: "common.white" }} /> : <LinkOffOutlined />}
          disabled={isPending}
        >
          Remove Link
        </Button>
      </DialogActions>
    </Dialog>
  );
}
